// src/components/StatusBarChart.tsx
//
// ★★★ WAVE 15 — P2C1 §12.83(k) "Charting" and §12.84(b) "Charts that offer drill
// down on detailed information". The RTM carried this clause pair as **not
// started** and that was only half right: `DashboardPage` already draws
// `pt-bar-track` meters for the bill-type composition. What it had was a
// magnitude picture with no axis, no part-to-whole context, no hover detail and
// **nowhere to click** — and §12.84(b)'s whole point is the click.
//
// ══ ★★ THE FORM WAS CHOSEN BEFORE THE COLOUR, AND THE COLOUR WAS COMPUTED ══
//
// Following the `dataviz` procedure rather than taste:
//
//  1. **The data's job is magnitude across a small set of NAMED, ORDERED
//     categories** — how many bills sit at each stage of the lifecycle. That is a
//     bar, not a pie: `anti-patterns.md` refuses a donut for comparing close
//     values, and two of these five figures are routinely within a few percent of
//     each other on a real estate.
//  2. **HORIZONTAL bars, not columns**, because the category labels are sentences
//     ("Awaiting level 2 approval") and a column chart would rotate them.
//  3. ⚠ **ONE HUE FOR EVERY BAR.** This is one series — a count — across
//     categories, and the category is carried by its own label. Colouring each bar
//     by its status is the *"value-ramp on nominal categories"* anti-pattern's
//     cousin and, here, something worse: it was **measured and refused**. Running
//     the skill's validator on this product's four SGDS status hues
//     (`#00405f` navy, `#7e6917` yellow-600, `#0e7c3d` green-600, `#cf2323`
//     red-600) against a white card:
//
//        [FAIL] CVD separation      worst adjacent #0e7c3d↔#7e6917 ΔE 4.3 (protan)
//        [FAIL] Normal-vision floor worst adjacent #0e7c3d↔#7e6917 ΔE 11.6 — below 15
//
//     A normal-vision floor under 15 is a hard fail that secondary encoding does
//     not excuse. Two of the five statuses (`AWAITING_L1`, `AWAITING_L2`) also
//     share the *identical* warning hue, so a status-coloured chart would encode
//     two categories in one colour while claiming to distinguish five.
//  4. **So the hue is `--pt-series-1`, which this product already owns** and which
//     the same validator passes on both surfaces:
//
//        light #2a78d6 on #ffffff → lightness PASS · chroma PASS · contrast PASS
//        dark  #3987e5 on #3b3b3b → lightness PASS · chroma PASS · contrast PASS
//
//     No new colour was invented, and the bill-type meters already on the
//     dashboard use the same token, so the two pictures do not disagree.
//
// ⚠ **The status's own colour is NOT lost** — the row label renders the product's
// `BillStatusBadge`, at text scale, where the SGDS semantic tokens were designed
// and contrast-checked to work. Identity therefore has three channels (the badge,
// the written label and the position) and colour-of-bar is not one of them.
//
// ══ ★★★ IT IS A TABLE, AND THAT IS THE ACCESSIBILITY DESIGN RATHER THAN A FALLBACK ══
//
// `dataviz`'s final pass asks for "a table view". Instead of a chart plus a
// separate table that can drift from it, **the chart IS a table**: one row per
// status, a real `<th>` per column, and the bar drawn inside the value cell. A
// screen reader gets the figures and the drill-down link with no `aria-label`
// paraphrasing the picture, and there is only one copy of the data.
//
// ⚠ It follows that this repository's standing rule applies — **every data table's
// headers are sortable** — so they are. The sort is CLIENT-SIDE and that is
// honest here for the reason `lib/useSort.ts`'s header gives: `byStatus` is a
// complete server-computed aggregate over the caller's whole scope, not a page of
// a longer list, so there is no second page for a reorder to be wrong about.
// `column: null` is the LIFECYCLE order, which is the order the domain reads in.
import { Link } from 'react-router'
import { useMemo, type ReactElement } from 'react'
import { SortableTh, SortAnnouncement } from './SortableTh'
import { useClientSort, type ClientSortColumns } from '../lib/useClientSort'
import { BillStatusBadge } from './StatusBadge'
import { billStatusLabel } from '../lib/format'

/**
 * One measured bar.
 *
 * ⚠ `count` is a count for the caller's WHOLE scope, and this component must not
 * be handed a page-derived figure — see the file header on why the status
 * breakdown qualifies and the bill-type breakdown does not.
 */
export interface StatusBar {
  readonly status: string
  readonly count: number
}

/** Chart geometry, in the SVG's own user units. */
const BAR_HEIGHT = 14
const BAR_RADIUS = 4
const TRACK_WIDTH = 240

/**
 * ★ One bar.
 *
 * ⚠ **Rounded at the DATA END only.** `marks-and-anatomy.md` asks for a 4px
 * rounded data-end "anchored to the baseline": a rect rounded on all four corners
 * detaches visually from its axis and reads as a floating pill. The rect is
 * therefore drawn starting `BAR_RADIUS` to the LEFT of the origin and clipped by
 * the viewBox, so the baseline end is square and only the end that carries the
 * value is round.
 */
function Bar({ count, max, label }: { count: number; max: number; label: string }): ReactElement {
  // ★★ TWO ZEROES, AND THEY ARE NOT THE SAME PICTURE.
  //
  //  · **A zero beside non-zero rows** (`max > 0`) gets a 2-unit stub. A
  //    zero-width rect draws nothing at all, and an absent bar reads as "no data"
  //    rather than "none at this stage" — the distinction this product has a
  //    standing rule about.
  //  · **An entirely empty estate** (`max === 0`) draws NO fill on any row. A
  //    uniform stub across every row would read as five equal small values, which
  //    is a picture of something that is not there. The track and the written
  //    figures carry it instead, and they say 0.
  const width = max <= 0 ? null : Math.max(2, Math.round((count / max) * TRACK_WIDTH))
  return (
    <svg
      viewBox={`0 0 ${TRACK_WIDTH} ${BAR_HEIGHT}`}
      width={TRACK_WIDTH}
      height={BAR_HEIGHT}
      preserveAspectRatio="none"
      className="pt-chart-bar"
      /*
        ⚠ `aria-hidden`, deliberately. The `<td>` this sits in already carries the
        figure as text and the row is a table row a screen reader reads in full;
        announcing the drawing as well would repeat every number twice.
      */
      aria-hidden="true"
      focusable="false"
    >
      {/* The native hover detail. A JS tooltip layer would be richer; `<title>`
          needs no listener, survives with scripting off, and is what a browser
          already knows how to place. */}
      <title>{label}</title>
      <rect x={0} y={0} width={TRACK_WIDTH} height={BAR_HEIGHT} rx={2} className="pt-chart-track" />
      {width !== null && (
        <rect
          x={-BAR_RADIUS}
          y={0}
          width={width + BAR_RADIUS}
          height={BAR_HEIGHT}
          rx={BAR_RADIUS}
          className="pt-chart-fill"
        />
      )}
    </svg>
  )
}

type ChartSortKey = 'status' | 'count'

const COLUMN_LABELS: Readonly<Record<ChartSortKey, string>> = {
  status: 'Status',
  count: 'Bills',
}

/**
 * ★★★ The chart.
 *
 * @param bars in LIFECYCLE order — the order the caller believes the domain reads
 *   in. This component never re-orders them by size on its own; a third click on
 *   a header returns to exactly this order.
 * @param total the scope's own total, used for the part-to-whole percentage. Taken
 *   rather than summed, so a total the server computed and a total this component
 *   derived can never silently differ.
 * @param drillDownTo builds the link for a bar. ⚠ Omit it and the rows render as
 *   plain text: a chart that LOOKS clickable and is not is worse than one that
 *   does not offer it.
 */
export function StatusBarChart({
  bars,
  total,
  drillDownTo,
}: {
  readonly bars: readonly StatusBar[]
  readonly total: number
  readonly drillDownTo?: (status: string) => string
}): ReactElement {
  /*
    ★★ A HAND-ROLLED COMPARATOR UNTIL 2026-08-24. Folded onto `useClientSort` for
    consistency rather than to fix a defect — both columns here are `NOT NULL` — and
    because the next comparator copied from this file would have inherited its lack of
    a null branch.

    ⚠ A browser sort is honest here for the reason `useClientSort`'s header requires:
    `bars` is one row per lifecycle STAGE over the caller's whole scope, a fixed and
    complete set that no route caps.

    ⚠ `status` sorts by the LABEL a reader sees (`billStatusLabel`), not by the stored
    code. The codes are `AWAITING_L1`, `AWAITING_L2` and so on; the labels are the
    words on screen, and a sort that ordered by the code would put the rows in an order
    the column does not appear to be in.
  */
  const chartColumns: ClientSortColumns<StatusBar, ChartSortKey> = useMemo(
    () => ({
      status: { type: 'text', valueOf: (bar) => billStatusLabel(bar.status) },
      count: { type: 'number', valueOf: (bar) => bar.count },
    }),
    [],
  )
  const { rows: ordered, sort, onSort, interacted } = useClientSort(bars, chartColumns)
  const max = bars.reduce((highest, bar) => Math.max(highest, bar.count), 0)

  return (
    <>
      <SortAnnouncement
        sort={sort}
        interacted={interacted}
        busy={false}
        columnLabels={COLUMN_LABELS}
        rowCount={ordered.length}
      />
      <div className="pt-table-wrap">
        <table className="pt-table pt-chart-table">
          <caption className="sr-only">
            Bills in your scope by lifecycle status, with each status's share of the total
          </caption>
          <thead>
            <tr>
              <SortableTh label={COLUMN_LABELS.status} sortKey="status" sort={sort} onSort={onSort} />
              <SortableTh label={COLUMN_LABELS.count} sortKey="count" sort={sort} onSort={onSort} align="right" />
              {/*
                ⚠ NOT sortable, and not an oversight. The share is `count / total`
                — a monotonic function of the column beside it — so a sort on it
                would be a second control producing the identical order, which is
                how a reader concludes the two columns measure different things.
              */}
              <th
                scope="col"
                data-unsortable="count divided by total — a monotonic function of the column beside it, so a sort here would be a second control producing the identical order"
              >
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((bar) => {
              const share = total > 0 ? (bar.count / total) * 100 : 0
              const detail = `${billStatusLabel(bar.status)}: ${bar.count} of ${total} bills (${share.toFixed(1)}%)`
              return (
                <tr key={bar.status}>
                  <td>
                    {/*
                      ⚠ The DRILL-DOWN (§12.84(b)), and it is on the label rather
                      than the drawing: a link whose accessible name is "Approved"
                      is one a screen-reader user can act on, and an SVG rect with
                      a click handler is not.
                    */}
                    {drillDownTo === undefined ? (
                      <span className="inline-flex items-center gap-2">
                        <BillStatusBadge status={bar.status} />
                      </span>
                    ) : (
                      <Link to={drillDownTo(bar.status)} className="inline-flex items-center gap-2 pt-ref-link">
                        <BillStatusBadge status={bar.status} />
                        <span className="text-xs">open these bills</span>
                      </Link>
                    )}
                  </td>
                  <td className="pt-num">{bar.count}</td>
                  <td>
                    <div className="flex items-center gap-3">
                      <Bar count={bar.count} max={max} label={detail} />
                      <span className="pt-tabular text-xs text-[var(--sgds-body-color-subtle)]">
                        {share.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
